import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { ThemeProvider } from "./contexts/ThemeContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { FacialScanProvider } from "./contexts/FacialScanContext";

// Pages
import WelcomePage from "./pages/WelcomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ConsentPage from "./pages/onboarding/ConsentPage";
import OnboardingWizard from "./pages/onboarding/OnboardingWizard";
import DevicePage from "./pages/onboarding/DevicePage";
import DashboardPage from "./pages/DashboardPage";
import Coach360Page from "./pages/Coach360Page";
import HabitsPage from "./pages/HabitsPage";
import TrendsPage from "./pages/TrendsPage";
import SettingsPage from "./pages/SettingsPage";
import BillingPage from "./pages/BillingPage";
import FAQPage from "./pages/FAQPage";
import ContactPage from "./pages/ContactPage";
import WeeklyReviewPage from "./pages/WeeklyReviewPage";
import EssentialsPage from "./pages/EssentialsPage";
import ConnectToCarePage from "./pages/ConnectToCarePage";

// Facial Scan Pages
import ScanStartPage from "./pages/scan/ScanStartPage";
import ScanInProgressPage from "./pages/scan/ScanInProgressPage";
import ScanCompletePage from "./pages/scan/ScanCompletePage";
import ScanErrorPage from "./pages/scan/ScanErrorPage";
import ManualEntryPage from "./pages/ManualEntryPage";
import ScanHistoryPage from "./pages/ScanHistoryPage";
import DailyPlanPage from "./pages/DailyPlanPage";
import DemoFlow from "./pages/DemoFlow";
import BriefingReturnPage from "./pages/BriefingReturnPage";
import EventAdminPage from "./pages/EventAdminPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import FindHelpPage from "./pages/FindHelpPage";
import SymptomsPage from "./pages/SymptomsPage";

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

// Public Route (redirects to dashboard if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user && user.onboarding_completed) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><WelcomePage /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      
      {/* Onboarding Routes */}
      <Route path="/onboarding/consent" element={<ProtectedRoute><ConsentPage /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingWizard /></ProtectedRoute>} />
      <Route path="/onboarding/device" element={<ProtectedRoute><DevicePage /></ProtectedRoute>} />
      
      {/* Main App Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/coach" element={<ProtectedRoute><Coach360Page /></ProtectedRoute>} />
      <Route path="/habits" element={<ProtectedRoute><HabitsPage /></ProtectedRoute>} />
      <Route path="/trends" element={<ProtectedRoute><TrendsPage /></ProtectedRoute>} />
      <Route path="/symptoms" element={<ProtectedRoute><SymptomsPage /></ProtectedRoute>} />
      <Route path="/care" element={<ProtectedRoute><ConnectToCarePage /></ProtectedRoute>} />
      <Route path="/find-help" element={<ProtectedRoute><FindHelpPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/settings/profile" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/settings/devices" element={<ProtectedRoute><DevicePage /></ProtectedRoute>} />
      <Route path="/settings/privacy" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/support/faq" element={<ProtectedRoute><FAQPage /></ProtectedRoute>} />
      <Route path="/support/contact" element={<ProtectedRoute><ContactPage /></ProtectedRoute>} />
      <Route path="/weekly-review" element={<ProtectedRoute><WeeklyReviewPage /></ProtectedRoute>} />
      <Route path="/essentials" element={<ProtectedRoute><EssentialsPage /></ProtectedRoute>} />
      <Route path="/daily-plan" element={<ProtectedRoute><DailyPlanPage /></ProtectedRoute>} />
      
      {/* Facial Scan Routes */}
      <Route path="/scan/start" element={<ProtectedRoute><ScanStartPage /></ProtectedRoute>} />
      <Route path="/scan/in-progress" element={<ProtectedRoute><ScanInProgressPage /></ProtectedRoute>} />
      <Route path="/scan/complete" element={<ProtectedRoute><ScanCompletePage /></ProtectedRoute>} />
      <Route path="/scan/error" element={<ProtectedRoute><ScanErrorPage /></ProtectedRoute>} />
      <Route path="/scan/history" element={<ProtectedRoute><ScanHistoryPage /></ProtectedRoute>} />
      <Route path="/manual-entry" element={<ProtectedRoute><ManualEntryPage /></ProtectedRoute>} />
      
      {/* Event/Demo Flow (public, no auth required) */}
      <Route path="/event" element={<DemoFlow />} />
      <Route path="/event/briefing/:token" element={<BriefingReturnPage />} />
      <Route path="/admin/events" element={<EventAdminPage />} />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <FacialScanProvider>
          <BrowserRouter>
            <div className="App">
              <AppRoutes />
              <Toaster 
                position="top-center" 
                richColors 
                closeButton
                toastOptions={{
                  className: 'font-sans',
                  duration: 3000,
                }}
              />
            </div>
          </BrowserRouter>
        </FacialScanProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
